/**
 * Coding jobs status list (design §11) — project-detail section showing the
 * mechanical flow `pending → provisioned → indexed → pr-open → done/failed`.
 *
 * Display + link-out only (P5-3): jobs are started by agents through their
 * `repositories.coding-job.*` verbs, so this board never mutates and offers
 * no create affordance (the empty state names the provenance instead).
 *
 * Freshness (§11.4): 30s polling while the document is visible AND any listed
 * job is non-terminal; all-terminal (or hidden) stops polling. Rows update in
 * place — background refresh shows no spinners (C2 boundary). The PR board
 * consumes the same cache (one fetch per projectId args).
 *
 * Section renders only when the project has ≥1 `type:'repository'` resource
 * (§5.1, G3) — otherwise it returns null.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Inbox } from 'lucide-react';

import { CopyButton } from '@/components/common/CopyButton';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useGetResourcesQuery } from '@/features/projects/api/projectsApi';

import { useGetCodingJobsQuery, useGetRepositoryQuery } from '../api/coderApi';
import type { CodingJobView } from '../types';
import { isTerminalJobStatus } from '../types';
import {
  derivePullRequestUrl,
  formatRelativeTime,
  fullTimestamp,
  providerLabel,
  useDocumentVisible,
} from '../lib';
import { JobStatusBadge } from './JobStatusBadge';

export interface CodingJobsListProps {
  projectId: string;
}

/** §11.4: poll only while visible and at least one job is in flight. */
const POLL_INTERVAL_MS = 30_000;

/** v1 jobs carry exactly one item (P5-T3 DTO); later N-repo jobs render the
 * job row from its first item — the table stays row-per-job (§11.1). */
function primaryItem(job: CodingJobView) {
  return job.items?.[0];
}

/** Repository name from the shared `getRepository` cache; unknown/loading id
 * falls back to the mono id (I2 data extremes — no spinner per cell). */
function RepoNameCell({ repositoryId }: { repositoryId?: string }) {
  const { data: repo } = useGetRepositoryQuery(repositoryId ?? '', { skip: !repositoryId });

  if (!repositoryId) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  return repo?.name ? (
    <span className="text-sm">{repo.name}</span>
  ) : (
    <span className="font-mono text-xs text-muted-foreground">{repositoryId}</span>
  );
}

/** `#128 ↗` external link when derivable (design §11.3 → same rules as §10.2). */
function JobPrLinkCell({ job }: { job: CodingJobView }) {
  const item = primaryItem(job);
  const repositoryId = item?.repositoryId;
  const { data: repo } = useGetRepositoryQuery(repositoryId ?? '', {
    skip: !repositoryId || item?.prNumber === undefined,
  });

  if (!item?.prNumber) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const url = derivePullRequestUrl(repo, item.prNumber);
  const number = <span className="tabular-nums">#{item.prNumber}</span>;
  if (!url) {
    return (
      <span className="inline-flex items-center gap-1 text-sm">
        {number}
        <span className="sr-only"> (link unavailable)</span>
      </span>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open pull request #${item.prNumber} on ${providerLabel(repo?.provider)}`}
      className="inline-flex min-h-10 items-center gap-1 rounded-md text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {number}
      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

export function CodingJobsList({ projectId }: CodingJobsListProps) {
  const resourcesQuery = useGetResourcesQuery({ projectId, type: 'repository' });
  const hasRepositories = (resourcesQuery.data ?? []).some((resource) => Boolean(resource.refId));

  // Polling gate reads the PREVIOUS data pass; it flips on as soon as the
  // first jobs payload lands (§11.4 — background refresh, no UI spinners).
  const [anyActive, setAnyActive] = useState(false);
  const visible = useDocumentVisible();
  const jobsQuery = useGetCodingJobsQuery(projectId, {
    skip: !hasRepositories,
    pollingInterval: visible && anyActive ? POLL_INTERVAL_MS : 0,
  });
  const jobs = useMemo(() => jobsQuery.data ?? [], [jobsQuery.data]);

  useEffect(() => {
    setAnyActive(jobs.some((job) => !isTerminalJobStatus(job.status)));
  }, [jobs]);

  const sortedJobs = useMemo(
    () =>
      [...jobs].sort(
        (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
      ),
    [jobs],
  );

  const retry = useCallback(() => {
    void resourcesQuery.refetch();
    void jobsQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch identities churn per render; harmless closure.
  }, [jobsQuery.isError, hasRepositories]);

  // Section visibility: repo-less projects omit the section entirely (§5.1/G3).
  if (!resourcesQuery.isLoading && !hasRepositories) {
    return null;
  }

  const columns: DataTableColumn<CodingJobView>[] = [
    {
      id: 'work-branch',
      header: 'Work branch',
      cell: (job) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="block max-w-[12rem] truncate font-mono text-xs">{job.workBranch}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs break-all">{job.workBranch}</TooltipContent>
        </Tooltip>
      ),
    },
    {
      id: 'repository',
      header: 'Repository',
      cell: (job) => <RepoNameCell repositoryId={primaryItem(job)?.repositoryId} />,
    },
    {
      id: 'source-key',
      header: 'Source key',
      cell: (job) => {
        const item = primaryItem(job);
        if (!item?.repositoryId) {
          return <span className="text-sm text-muted-foreground">—</span>;
        }
        // Q4: use the server's sourceKey; derive the same string if absent.
        const sourceKey = item.sourceKey ?? `${item.repositoryId}:${job.workBranch}`;
        return (
          <span className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block max-w-[14rem] truncate font-mono text-xs">{sourceKey}</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs break-all">{sourceKey}</TooltipContent>
            </Tooltip>
            <CopyButton value={sourceKey} label="Copy source key" />
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: (job) => <JobStatusBadge status={job.status} />,
    },
    {
      id: 'pull-request',
      header: 'Pull request',
      cell: (job) => <JobPrLinkCell job={job} />,
    },
    {
      id: 'updated',
      header: 'Updated',
      cell: (job) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="block text-sm tabular-nums text-muted-foreground">
              {formatRelativeTime(job.updatedAt)}
            </span>
          </TooltipTrigger>
          <TooltipContent>{fullTimestamp(job.updatedAt)}</TooltipContent>
        </Tooltip>
      ),
    },
  ];

  return (
    <section aria-labelledby="coding-jobs-title" className="mt-6">
      <h2 id="coding-jobs-title" className="text-lg font-semibold">
        Coding jobs
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Each job works on its own branch and opens a pull request when it's done.
      </p>

      {jobsQuery.isLoading ? (
        <LoadingRows label="Loading coding jobs…" columns={6} className="mt-4" />
      ) : jobsQuery.isError ? (
        <QueryErrorView error={jobsQuery.error} label="coding jobs" onRetry={retry} />
      ) : sortedJobs.length === 0 ? (
        <EmptyState
          icon={<Inbox />}
          title="No coding jobs"
          description="Jobs your coding agents start appear here."
          className="mt-4"
        />
      ) : (
        <DataTable
          columns={columns}
          data={sortedJobs}
          pageSize={10}
          getRowId={(job) => job.id}
          className="mt-4"
        />
      )}
    </section>
  );
}
